import { useState, useEffect, useRef } from 'react';
import QrScanner from 'qr-scanner';
import { 
  isMobileDevice, 
  isIOSDevice, 
  isHTTPS, 
  checkCameraPermissionStatus,
  getIOSErrorMessage,
  saveCameraPermission,
  getSavedCameraPermission
} from '../utils/iosHelpers';
import { getStoredLanguage, normalizeLanguage } from '../utils/language';
// import IOSNotice from './IOSNotice'; // iOS bilgilendirme mesajları devre dışı

const QRScanner = ({ onScan, onClose, isOpen = true }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('unknown');
  const [deviceInfo, setDeviceInfo] = useState({});
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(getStoredLanguage());

  const tl = (tr, en, ru) => {
    if (currentLanguage === 'EN') return en;
    if (currentLanguage === 'RU') return ru || en || tr;
    return tr;
  };

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const qrScannerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      return undefined;
    }

    try {
      setCurrentLanguage(getStoredLanguage());
    } catch (_) {}
    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') window.addEventListener('app-language-change', handler);

    // Cihaz bilgilerini topla
    const deviceInfo = {
      isIOS: isIOSDevice(),
      isAndroid: isMobileDevice(), // isAndroidDevice removed
      isMobile: isMobileDevice(),
      isHTTPS: isHTTPS(),
      userAgent: navigator.userAgent,
      platform: navigator.platform
    };
    setDeviceInfo(deviceInfo);

    // Kamera varlığını kontrol et ve otomatik başlat
    const initializeCamera = async () => {
      await checkCameraAvailability();
      
      // Kamera izni verilmişse otomatik başlat
      if (cameraPermission === 'granted' || cameraPermission === 'prompt') {
        try {
          await startCamera();
        } catch (error) {
          console.log('Otomatik kamera başlatma başarısız:', error);
        }
      }
    };

    initializeCamera();

    console.log('QRScanner mount - Cihaz bilgileri:', deviceInfo);

    return () => {
      console.log('QRScanner cleanup çalışıyor');
      stopCamera();
      if (typeof window !== 'undefined') window.removeEventListener('app-language-change', handler);
    };
  }, [isOpen]);

  const checkCameraAvailability = async () => {
    try {
      console.log('Kamera kontrolü başlatılıyor...');
      setError(null);

      // Kamera izni kontrolü
      const permissionStatus = await checkCameraPermissionStatus();
      setCameraPermission(permissionStatus);

      if (permissionStatus === 'granted') {
        console.log('Kamera izni verildi, kamera başlatılıyor...');
        setHasCamera(true);
        await startCamera();
      } else if (permissionStatus === 'prompt') {
        console.log('Kamera izni istenecek...');
        setHasCamera(true);
        // İzin istenebilir durumda otomatik izin iste
        try {
          await requestCameraPermissionHandler();
        } catch (error) {
          console.log('Otomatik izin isteme başarısız:', error);
        }
      } else if (permissionStatus === 'denied') {
        setHasCamera(false);
        setError(tl(
          'Kamera erişimi reddedildi. Tarayıcı ayarlarından izni kontrol edin.',
          'Camera access denied. Please check your browser settings.',
          'Доступ к камере отклонён. Проверьте разрешения в настройках браузера.'
        ));
      } else {
        setHasCamera(false);
        setError(tl(
          'Kamera erişimi mevcut değil.',
          'Camera access not available.',
          'Доступ к камере недоступен.'
        ));
      }

    } catch (error) {
      console.error('Kamera kontrolü hatası:', error);
      setHasCamera(false);
      setCameraPermission('denied');
      setError(tl(
        'Kamera kontrolü başarısız. Lütfen sayfayı yenileyin.',
        'Camera check failed. Please refresh the page.',
        'Проверка камеры не удалась. Пожалуйста, обновите страницу.'
      ));
    }
  };

  const requestCameraPermissionHandler = async () => {
    try {
      // Daha önce izin verilmiş mi kontrol et
      const savedPermission = getSavedCameraPermission();
      if (savedPermission === 'granted') {
        setCameraPermission('granted');
        setHasCamera(true);
        return;
      }

      setIsRequestingPermission(true);
      setError(null);

      console.log('Kamera izni isteniyor...');
      
      // Basit kamera izni isteme
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Stream'i hemen kapat (sadece izin için)
      stream.getTracks().forEach(track => track.stop());
      
      // İzni kaydet ve state'i güncelle
      saveCameraPermission('granted');
      setCameraPermission('granted');
      setHasCamera(true);
      console.log('Kamera izni verildi');

      // İzin verildikten sonra kamerayı başlat
      await startCamera();

    } catch (error) {
      console.error('Kamera izni hatası:', error);
      setCameraPermission('denied');
      setHasCamera(false);
      setError(tl(
        'Kamera izni reddedildi. Tarayıcı ayarlarından izni kontrol edin.',
        'Camera permission denied. Please check your browser settings.',
        'Разрешение на камеру отклонено. Проверьте разрешения в настройках браузера.'
      ));
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const startCamera = async () => {
    try {
      console.log('Kamera başlatılıyor...');
      setIsScanning(true);
      setError(null);

      // Video elementini kontrol et
      if (!videoRef.current) {
        throw new Error('Video elementi bulunamadı');
      }

      // Basit kamera stream başlatma
      const constraints = {
        video: {
          facingMode: 'environment', // Arka kamera
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 }
        }
      };

      console.log('Kamera constraints:', constraints);

      // Kamera stream'ini başlat
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (!stream) {
        throw new Error('Kamera stream başlatılamadı');
      }

      // Stream'i video elementine bağla
      videoRef.current.srcObject = stream;
      streamRef.current = stream;
      setHasCamera(true);

      // Video hazır olduğunda QR scanner'ı başlat
      const onVideoReady = () => {
        console.log('Video hazır, QR scanner başlatılıyor...');
        setVideoReady(true);

        try {
          // QR Scanner seçenekleri
          const scannerOptions = {
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 3,
            returnDetailedScanResult: true
          };

          // QR Scanner'ı başlat
          qrScannerRef.current = new QrScanner(
            videoRef.current,
            (result) => {
              console.log('QR kod tarandı:', result);
              handleQRResult(result.data);
            },
            scannerOptions
          );

          qrScannerRef.current.start()
            .then(() => {
              console.log('QR Scanner başarıyla başlatıldı');
              setIsScanning(true);
            })
            .catch((error) => {
              console.error('QR Scanner başlatma hatası:', error);
              setError(tl(
                `QR Tarayıcı başlatılamadı: ${error.message}`,
                `Failed to start QR Scanner: ${error.message}`,
                `Не удалось запустить QR-сканер: ${error.message}`
              ));
              setIsScanning(false);
            });

        } catch (error) {
          console.error('QR Scanner oluşturma hatası:', error);
          setError(tl(
            `QR Tarayıcı oluşturulamadı: ${error.message}`,
            `Failed to initialize QR Scanner: ${error.message}`,
            `Не удалось инициализировать QR-сканер: ${error.message}`
          ));
          setIsScanning(false);
        }
      };

      // Video yüklendiğinde callback
      if (videoRef.current.readyState >= 2) {
        onVideoReady();
      } else {
        videoRef.current.addEventListener('loadedmetadata', onVideoReady, { once: true });
      }

    } catch (error) {
      console.error('Kamera başlatma hatası:', error);
      setError(tl(
        `Kamera başlatılamadı: ${error.message}`,
        `Failed to start camera: ${error.message}`,
        `Не удалось запустить камеру: ${error.message}`
      ));
      setIsScanning(false);
      setHasCamera(false);
    }
  };

  const stopCamera = () => {
    console.log('stopCamera çağrıldı');
    
    // 1. QR Scanner'ı durdur
    if (qrScannerRef.current) {
      try { qrScannerRef.current.stop(); } catch (e) { console.log('QR stop error:', e); }
      try { qrScannerRef.current.destroy(); } catch (e) { console.log('QR destroy error:', e); }
      qrScannerRef.current = null;
    }
    
    // 2. Stream'i durdur
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(track => {
          console.log('Track durduruluyor:', track.kind, track.label);
          track.stop();
        });
      } catch (e) { console.log('Stream stop error:', e); }
      streamRef.current = null;
    }
    
    // 3. Video element'ini temizle
    if (videoRef.current) {
      try {
        // Video element'indeki stream'i de durdur (ekstra güvenlik)
        const videoStream = videoRef.current.srcObject;
        if (videoStream && videoStream.getTracks) {
          videoStream.getTracks().forEach(track => {
            console.log('Video track durduruluyor:', track.kind);
            track.stop();
          });
        }
        videoRef.current.srcObject = null;
        videoRef.current.pause();
      } catch (e) { console.log('Video cleanup error:', e); }
    }
    
    setIsScanning(false);
    setVideoReady(false);
    console.log('Kamera tamamen durduruldu');
    setVideoReady(false);
  };

  const handleQRResult = (qrData) => {
    console.log('QR verisi işleniyor:', qrData);
    
    // QR kod içeriğini satırlara böl
    const lines = qrData.split('\n').filter(line => line.trim());
    console.log('QR kod satırları:', lines);
    
    // İlk satırda tam URL var mı kontrol et
    if (lines.length > 0 && lines[0].startsWith('https://')) {
      console.log('Tam link QR kodu tespit edildi:', lines[0]);
      try {
        const url = new URL(lines[0]);
        const path = url.pathname;
        console.log('Yeni URL oluşturuldu:', path);
        stopCamera();
        onScan(path); // Pass the path to onScan
        onClose();
        return;
      } catch (error) {
        console.error('URL parse hatası:', error);
      }
    }
    
    // Eski format QR kodları için uyumluluk (KM_101 gibi)
    if (qrData && qrData.includes('_')) {
      console.log('Eski format QR kodu tespit edildi:', qrData);
      const parts = qrData.split('_');
      if (parts.length === 2) {
        const muzeCode = parts[0].toLowerCase();
        const sectionNo = parts[1];
        const newUrl = `/muze/${muzeCode}/${sectionNo}`; // Construct relative path
        console.log('Yeni URL oluşturuldu:', newUrl);
        stopCamera();
        onScan(newUrl); // Pass the relative path to onScan
        onClose();
        return;
      }
    }
    
      // Satırlarda eski format kod var mı kontrol et
    for (const line of lines) {
      if (line.includes('_')) {
        const parts = line.split('_');
        if (parts.length === 2) {
          const muzeCode = parts[0].toLowerCase();
          const sectionNo = parts[1];
          const newUrl = `/muze/${muzeCode}/${sectionNo}`;
          console.log('Satırlardan eski format tespit edildi, yeni URL:', newUrl);
          stopCamera();
          onScan(newUrl);
          onClose();
          return;
        }
      }
    }    console.log('Bilinmeyen QR kod formatı:', qrData);
    stopCamera();
    onScan(qrData);
    onClose();
  };

  const getStatusMessage = () => {
    if (isIOSDevice() && !isHTTPS()) {
      return tl(
        'iOS cihazlarda kamera erişimi için HTTPS bağlantısı gereklidir.',
        'HTTPS connection is required for camera access on iOS.',
        'Для доступа к камере на iOS требуется HTTPS-соединение.'
      );
    }
    
    if (cameraPermission === 'granted') {
      return tl(
        'Kamera hazır. QR kodları tarayabilirsiniz.',
        'Camera ready. You can scan QR codes.',
        'Камера готова. Можно сканировать QR-коды.'
      );
    } else if (cameraPermission === 'denied') {
      return tl(
        'Kamera erişimi reddedildi. Tarayıcı ayarlarından izni kontrol edin.',
        'Camera access denied. Please check your browser settings.',
        'Доступ к камере отклонён. Проверьте разрешения в настройках браузера.'
      );
    } else if (cameraPermission === 'prompt') {
      return tl(
        'Kamera izni istenecek. İzin verdiğinizde arka kamera kullanılacaktır.',
        'Camera permission will be requested. The rear camera will be used after granting access.',
        'Будет запрошено разрешение на камеру. После разрешения будет использована основная камера.'
      );
    } else {
      return tl(
        'Kamera durumu kontrol ediliyor...',
        'Checking camera status...',
        'Проверяется состояние камеры...'
      );
    }
  };

  const getStatusColor = () => {
    if (isIOSDevice() && !isHTTPS()) return 'red';
    if (cameraPermission === 'granted') return 'green';
    if (cameraPermission === 'denied') return 'red';
    if (cameraPermission === 'prompt') return 'yellow';
    return 'blue';
  };

  const canStartCamera = () => {
    return cameraPermission === 'granted' && !isScanning && !isRequestingPermission;
  };

  const canRequestPermission = () => {
    return cameraPermission === 'prompt' && !isRequestingPermission;
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{tl('QR Kod Tarayıcı', 'QR Code Scanner', 'Сканер QR-кодов')}</h2>
      </div>

      {/* Camera View */}
      <div className="relative bg-black rounded-lg overflow-hidden mb-6" style={{ aspectRatio: '4/3' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          controls={false}
          className={`w-full h-full object-cover ${isScanning ? 'block' : 'hidden'}`}
        />

        {isScanning && (
          <>
            {/* Scanning Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 border-2 border-white rounded-lg relative">
                <div className="absolute inset-0 border-2 border-blue-500 rounded-lg animate-pulse"></div>
              </div>
            </div>

            {/* Video Status Indicator */}
            {!videoReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p>{tl('Video yükleniyor...', 'Loading video...', 'Видео загружается...')}</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="space-y-4">
        {/* Kamera Durumu - Kaldırıldı */}
        {/* 
        <div className={`mb-6 p-4 rounded-lg border ${
          getStatusColor() === 'green' ? 'bg-green-50 border-green-200' : 
          getStatusColor() === 'red' ? 'bg-red-50 border-red-200' : 
          getStatusColor() === 'yellow' ? 'bg-yellow-50 border-yellow-200' :
          'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-start">
            <svg className={`h-5 w-5 mt-0.5 mr-3 flex-shrink-0 ${
              getStatusColor() === 'green' ? 'text-green-400' : 
              getStatusColor() === 'red' ? 'text-red-400' : 
              getStatusColor() === 'yellow' ? 'text-yellow-400' :
              'text-blue-400'
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm">
              <p className={`font-medium mb-1 ${
                getStatusColor() === 'green' ? 'text-green-800' : 
                getStatusColor() === 'red' ? 'text-red-800' : 
                getStatusColor() === 'yellow' ? 'text-yellow-800' :
                'text-blue-800'
              }`}>
                Kamera Durumu
              </p>
              <p className={
                getStatusColor() === 'green' ? 'text-green-700' : 
                getStatusColor() === 'red' ? 'text-red-700' : 
                getStatusColor() === 'yellow' ? 'text-yellow-700' :
                'text-blue-700'
              }>
                {getStatusMessage()}
              </p>
            </div>
          </div>
        </div>
        */}

        {/* İzin İsteme Butonu */}
        {canRequestPermission() && (
          <button
            onClick={requestCameraPermissionHandler}
            disabled={isRequestingPermission}
            className="w-full btn-primary py-3"
          >
            {isRequestingPermission ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {tl('İzin İsteniyor...', 'Requesting permission...', 'Запрашивается разрешение...')}
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                </svg>
                {tl('Kamera İzni İste', 'Request Camera Access', 'Запросить доступ к камере')}
              </>
            )}
          </button>
        )}

        {/* Kamera Başlatma Butonu */}
        {!isScanning ? (
          <button
            onClick={startCamera}
            className="w-full btn-primary py-3"
            disabled={!canStartCamera()}
          >
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            </svg>
            {canStartCamera()
              ? tl('QR Kod Taramayı Başlat', 'Start QR Scan', 'Начать сканирование QR')
              : tl('Kamera Erişimi Yok', 'No Camera Access', 'Нет доступа к камере')}
          </button>
        ) : (
          <button
            onClick={stopCamera}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            {tl('Taramayı Durdur', 'Stop Scanning', 'Остановить сканирование')}
          </button>
        )}

        {/* Kullanım Talimatları */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800 text-center">
            {tl(
              'QR kodu tarama alanının içinde tutun. Kod otomatik olarak algılanacaktır.',
              'Hold the QR code within the scanning area. The code will be detected automatically.',
              'Держите QR-код в зоне сканирования. Код будет распознан автоматически.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;

